import React, { useEffect, useState } from 'react';
import {
    Badge,
    Box,
    Icon,
    Heading,
    Stack,
    Text,
    useColorModeValue
} from '@chakra-ui/react';
import DataTable from 'react-data-table-component';
import DataTableExtensions from 'react-data-table-component-extensions';
import 'react-data-table-component-extensions/dist/index.css';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getPermisos, reset } from '../../features/permisoSlice';
import { ToastChakra } from '../../helpers/toast';
import { FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight } from 'react-icons/fi';
import { customStyles } from '../../helpers/customStyles';
import { ModalAgregarPermiso } from './ModalAgregarPermiso';
import { ModalDetallesPermiso } from './ModalDetallesPermiso';
import { ModalEditarPermiso } from './ModalEditarPermiso';
import { AlertEliminarPermiso } from './AlertEliminarPermiso';
import '../../theme/solarizedTheme';
import { Loading } from '../../helpers/Loading';
import Moment from 'moment';

const Permiso = ({ isRTL = false, ...props }) => {
    const direction = isRTL ? 'rtl' : 'ltr';

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const themeTable = useColorModeValue('default', 'solarized');

    const { user } = useSelector((state) => state.auth);

    const { permisos, isError,message, isLoading, totalRows } = useSelector((state) => state.permiso);

    const [perPage, setPerPage] = useState(10);
    const [page, setPage] = useState(1);

    useEffect(() => {
        if (!user) {
            navigate("/login");
            return;
        }
        dispatch(getPermisos({ page: 1, perPage })); // Solo llamamos la primera vez o si cambia `perPage`

        return () => {
            dispatch(reset()); // Reseteamos al desmontar el componente
        };
    }, [user, navigate, dispatch, perPage]);
    
    useEffect(() => {
        if (isError ) {
            ToastChakra('Error', message, 'error', 3000);
            console.log(message);
        }
    }, [isError, message]);

    const columns = [
        {
            name: 'ID',
            selector: row => row?.id || 'N/A',
            sortable: true,
            cellExport: row => row?.id || 'N/A',
            omit: true // Esto oculta la columna
        },
        {
            name: 'NOMBRE',
            selector: row => row?.key,
            sortable: true,
            cellExport: row => row?.key,
       
        },
        {
            name: 'DESCRIPCION',
            selector: row => row?.descripcion,
            sortable: true,
            cellExport: row => row?.descripcion,
       
        },
        {
            name: 'FECHA ALTA',
            selector: row => Moment(row?.createdAt).format('DD-MM-YYYY hh:mm A'),
            sortable: true,
            cellExport: row => Moment(row?.createdAt).format('DD-MM-YYYY hh:mm A'),
            resizable: true,

        },
  
        {
            name: 'ESTADO',
            selector: row => { return row.estado === true ? 'ACTIVO' : 'INACTIVO' },
            sortable: true,
            cellExport: row => row.estado === true ? 'ACTIVO' : 'INACTIVO',
            center: true,
            cell: row => (
                <div>
                    <Badge
                        colorScheme={row.estado === true ? 'green' : 'red'}
                        variant="solid"
                        w={24}
                        textAlign="center"
                        py={2}
                        rounded="full"
                    >
                        {row.estado === true ? 'ACTIVO' : 'INACTIVO'}
                    </Badge>
                </div>
            )
        },
        {
            name: 'ACCIONES',
            export: false,
            center: true,
            cell: row => (
                <div>
                    <ModalDetallesPermiso permiso={row} />
                    <ModalEditarPermiso row={row} page={page} perPage={perPage} />
                    <AlertEliminarPermiso row={row} page={page} perPage={perPage} />
                </div>
            ),
            width: '220px'
        }
    ]

    const handlePageChange = (newPage) => {
        setPage(newPage); // Actualiza el estado local de la página
        dispatch(getPermisos({ page: newPage, perPage })); // Despacha la acción con la nueva página
    };

    const handleRowsPerPageChange = (newPerPage) => {
        setPerPage(newPerPage); // Cambiamos el número de filas por página
        dispatch(getPermisos({ page: 1, perPage: newPerPage })); // Reiniciamos a la primera página
    };

    const tableData = {
        columns: columns,
        data: permisos,
    }

    if(isLoading) {
        return <Loading />
    }

    return (
        <div style={{ direction }} {...props}>

            <Stack spacing={4} direction="row" justifyContent="space-between" py={4} px={4}>
                <Heading size="lg">Permisos</Heading>
                <ModalAgregarPermiso page={page} perPage={perPage} />
            </Stack>
            <Box
                overflow="hidden"
                boxShadow={'base'}
                bg="white"
                _dark={{ bg: "primary.1000" }}
                mt={2}
                pt={2}
                rounded="2xl"
            >
                <DataTableExtensions
                    {...tableData}
                    print={false}
                    exportHeaders={true}
                    filterPlaceholder="BUSCAR"
                    numberOfColumns={columns.length}
                    fileName={'CIUDADES_' + new Date().toLocaleDateString()}
                >
                    <DataTable
                        defaultSortField="createdAt"
                        defaultSortAsc={false}
                        defaultSortOrder="desc"
                        pagination
                        paginationIconFirstPage={<Icon as={FiChevronsLeft} boxSize={6} _dark={{ color: "gray.300" }} />}
                        paginationIconLastPage={<Icon as={FiChevronsRight} boxSize={6} _dark={{ color: "gray.300" }} />}
                        paginationIconPrevious={<Icon as={FiChevronLeft} boxSize={6} _dark={{ color: "gray.300", _hover: { color: "white" } }} />}
                        paginationIconNext={<Icon as={FiChevronRight} boxSize={6} _dark={{ color: "gray.300", _hover: { color: "white" } }} />}
                        paginationServer
                        paginationPerPage={perPage}
                        onChangeRowsPerPage={handleRowsPerPageChange}
                        paginationRowsPerPageOptions={[5, 10, 15, 20, 25, 30, 50]}
                        paginationDefaultPage={page}
                        paginationTotalRows={totalRows}
                        onChangePage={handlePageChange}
                        paginationComponentOptions={{
                            rowsPerPageText: 'Filas por página:',
                            rangeSeparatorText: 'de',
                            noRowsPerPage: false,
                            selectAllRowsItem: true,
                            selectAllRowsItemText: 'Todos',
                        }}
                        theme={themeTable}
                        customStyles={customStyles}
                        pointerOnHover={true}
                        responsive={true}
                        noDataComponent={<Text mb={4} fontSize="lg">NO SE ENCONTRARON DATOS</Text>}
                    />
                </DataTableExtensions>
            </Box>
       </div> 
    );
}

export default Permiso;